import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaFacebookF, FaTwitter, FaLinkedinIn, FaInstagram } from "react-icons/fa";
import SecurityNotice from "./SecurityNotice";
import brandLogo from "../assets/brandLogo.png";
import { NBFCLink } from "../utils/nbfcLink";

const Footer = () => {
  // Graceful fallbacks for environment variables to ensure 100% accuracy and no runtime errors
  const companyName = (import.meta.env.VITE_COMPANY_NAME || "").trim();
  const companyPhone = (import.meta.env.VITE_COMPANY_PHONE || "").trim();
  const companyEmail = (import.meta.env.VITE_COMPANY_EMAIL || "").trim();
  const companyAddress = (import.meta.env.VITE_COMPANY_ADDRESS || "").trim();
  const companyNBFC = (import.meta.env.VITE_COMPANY_NBFC || "").trim();
  const companyNBFCLicense = (import.meta.env.VITE_COMPANY_NBFC_LICENSE || "").trim();

  const socialProfiles = [
    { icon: <FaFacebookF />, href: import.meta.env.VITE_COMPANY_FACEBOOK_URL },
    { icon: <FaInstagram />, href: import.meta.env.VITE_COMPANY_INSTAGRAM_URL },
    { icon: <FaLinkedinIn />, href: import.meta.env.VITE_COMPANY_LINKEDIN_URL },
    { icon: <FaTwitter />, href: import.meta.env.VITE_COMPANY_TWITTER_URL }
  ].filter((social) => {
    if (!social.href) return false;
    const url = social.href.trim();
    return url !== "" && url !== "#";
  });

  const hasSocial = socialProfiles.length > 0;
  const col1Span = hasSocial ? "col-span-1 md:col-span-4" : "col-span-1 md:col-span-5";
  const col2Span = hasSocial ? "col-span-1 md:col-span-2" : "col-span-1 md:col-span-3";
  const col3Span = hasSocial ? "col-span-1 md:col-span-3" : "col-span-1 md:col-span-4";

  return (
    <>
      <SecurityNotice />

      <footer className="w-full pt-16 pb-8 px-6 lg:px-20 font-sans relative z-10 select-none bg-white border-t border-slate-200/80 overflow-hidden">
        <div className="max-w-[1300px] mx-auto flex flex-col gap-10 relative z-10">

          {/* Main Content Layout */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 items-start">

            {/* Column 1: Logo, Contact Details & Certifications (4 cols) */}
            <div className={`${col1Span} flex flex-col items-start gap-6 text-left`}>
              <div className="flex justify-start w-full">
                <img
                  src={brandLogo}
                  alt={companyName}
                  className="h-11 sm:h-12 w-auto object-contain"
                />
              </div>

              {/* Contact Information */}
              <ul className="flex flex-col gap-4 text-slate-600 text-[14px] w-full">
                <li className="flex items-center gap-3">
                  <FaEnvelope className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <a href={`mailto:${companyEmail}`} className="hover:text-slate-900 transition-colors duration-200">
                    {companyEmail}
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <FaPhone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span>{companyPhone}</span>
                </li>
                <li className="flex items-start gap-3">
                  <FaMapMarkerAlt className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{companyAddress}</span>
                </li>
              </ul>

              {/* Badges */}
              <div className="flex items-center gap-3 text-slate-400 text-[13px] font-medium mt-2">
                <span>256-bit SSL</span>
                <span className="text-slate-200">|</span>
                <span>Made in India 🇮🇳</span>
              </div>
            </div>

            {/* Column 2: Quick Links (2 cols) */}
            <div className={`${col2Span} flex flex-col gap-5 text-left`}>
              <h3 className="text-slate-900 font-semibold text-[15px] tracking-wide">
                Quick Links
              </h3>
              <ul className="flex flex-col gap-3.5 text-[14px]">
                {[
                  { label: "Loan Calculator", href: "/#loan-calculator" },
                  { label: "About Us", href: "/about" },
                  { label: "FAQ", href: "/faq" },
                  { label: "Home", href: "/" },
                  { label: "How it Works", href: "/#how-it-works" },
                  { label: "Contact Us", href: "/contact" }
                ].map((link, idx) => (
                  <li key={idx}>
                    <a href={link.href} className="text-slate-500 hover:text-slate-900 font-medium transition-colors duration-200">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3: Legal (3 cols) */}
            <div className={`${col3Span} flex flex-col gap-5 text-left`}>
              <h3 className="text-slate-900 font-semibold text-[15px] tracking-wide">
                Legal
              </h3>
              <ul className="flex flex-col gap-3.5 text-[14px]">
                {[
                  { label: "Terms & Conditions", href: "/terms-condition" },
                  { label: "Privacy Policy", href: "/privacy-policy" },
                  { label: "Interest Rate Policy", href: "/interest-rate-policy" },
                  { label: "Risk Management Policy", href: "/risk-management-policy" },
                  { label: "KYC & AML Policy", href: "/kyc-aml-policy" },
                  { label: "Corporate Governance", href: "/corporate-governance" },
                  { label: "Fair Practice Code", href: "/fair-practice-code" },
                  { label: "Cancellation & Refund Policy", href: "/cancellation-refund-policy" }
                ].map((link, idx) => (
                  <li key={idx}>
                    <a href={link.href} className="text-slate-500 hover:text-slate-900 font-medium transition-colors duration-200">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 4: Social Profiles (3 cols) */}
            {socialProfiles.length > 0 && (
              <div className="col-span-1 md:col-span-3 flex flex-col gap-5 text-left">
                <h3 className="text-slate-900 font-semibold text-[15px] tracking-wide">
                  Social Profiles
                </h3>
                <div className="flex gap-2">
                  {socialProfiles.map((social, idx) => (
                    <a
                      key={idx}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition-all duration-200 hover:scale-[1.03]"
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Divider line */}
          <div className="border-t border-slate-200/80 w-full mt-4" />

          {/* NBFC & Copyright Row */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-slate-500 justify-between w-full">
            {companyNBFCLicense && (
              <div className="flex items-center gap-1.5 whitespace-nowrap">
                <span>RBI NBFC License:</span>
                <span className="font-semibold text-slate-800">{companyNBFCLicense}</span>
              </div>
            )}

            {companyNBFCLicense && (companyNBFC || companyName) && (
              <span className="hidden md:inline text-slate-300">|</span>
            )}

            {companyNBFC && (
              <div className="flex items-center gap-1.5 whitespace-nowrap">
                <span>Powered by:</span>
                <NBFCLink className="font-semibold text-slate-800 hover:text-[var(--primary)] hover:underline" />
              </div>
            )}

            {companyNBFC && companyName && (
              <span className="hidden md:inline text-slate-300">|</span>
            )}

            {companyName && (
              <div className="flex items-center whitespace-nowrap">
                <span>
                  Copyright © <span className="font-semibold text-slate-800">{companyName}</span>. All rights reserved.
                </span>
              </div>
            )}
          </div>

        </div>
      </footer>

      {/* Bottom Scrolling Caution Ticker Banner */}
      <div className="w-full bg-white text-black py-3 select-none overflow-hidden relative flex items-center border-t border-slate-200 z-20">
        <div className="animate-marquee-footer whitespace-nowrap flex gap-4 text-[13px] sm:text-[14px] font-semibold tracking-wide">
          <span>Secure Repayment on Website Link for loan payments. Do not make direct bank payments to fake UPI links or unauthorised payment links. <NBFCLink className="underline" /> is not responsible for payments made to external/unauthorised accounts.</span>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;&nbsp;</span>
          <span>Secure Repayment on Website Link for loan payments. Do not make direct bank payments to fake UPI links or unauthorised payment links. <NBFCLink className="underline" /> is not responsible for payments made to external/unauthorised accounts.</span>
        </div>
        <style dangerouslySetInnerHTML={{
          __html: `
          @keyframes marquee-footer {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee-footer {
            animation: marquee-footer 25s linear infinite;
            display: inline-block;
            width: max-content;
          }
        `}} />
      </div>
    </>
  );
};

export default Footer;
